import React, { useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import Button from '../../components/Buttons/Button';
import './register.css';

interface RegisterProps extends WithTranslation {}

const Register: React.FC<RegisterProps> = ({ t }) => {
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    agree: false,
  });

  const [errors, setErrors] = useState({
    name: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });

  const validate = (name: string, value: string) => {
    const newErrors: any = { ...errors };
    switch (name) {
      case 'name':
        if (!value) {
          newErrors.name = t('First_Name_Is_Required');
        } else if (value.length <= 3) {
          newErrors.name = t('First_Name_Must_Be_At_Least_3_Letters');
        } else {
          newErrors.name = '';
        }
        break;
      case 'lastName':
        if (!value) {
          newErrors.lastName = t('Last_Name_Is_Required');
        } else if (value.length <= 3) {
          newErrors.lastName = t('Last_Name_Must_Be_At_Least_3_Letters');
        } else {
          newErrors.lastName = '';
        }
        break;
      case 'email':
        if (!value) {
          newErrors.email = t('Email_Is_Required');
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          newErrors.email = t('Email_Is_Not_Valid');
        } else {
          newErrors.email = '';
        }
        break;
      case 'password':
        if (!value) {
          newErrors.password = t('Password_Is_Required');
        } else if (value.length < 8) {
          newErrors.password = t('Password_Must_Be_At_Least_8_Characters_Long');
        } else if (!/[A-Z]/.test(value)) {
          newErrors.password = t('Password_Must_Contain_At_Least_One_Uppercase_Letter');
        } else if (!/[a-z]/.test(value)) {
          newErrors.password = t('Password_Must_Contain_At_Least_One_Lowercase_Letter');
        } else if (!/[0-9]/.test(value)) {
          newErrors.password = t('Password_Must_Contain_At_Least_One_Number');
        } else if (!/[!@#$%^&*]/.test(value)) {
          newErrors.password = t('Password_Must_Contain_At_Least_One_Special_Character');
        } else {
          newErrors.password = '';
        }
        break;
      case 'phone':
        if (!value) {
          newErrors.phone = t('Phone_Is_Required');
        } else if (!/^\d{10}$/.test(value)) {
          newErrors.phone = t('Phone_Number_Must_Be_10_Digits');
        } else {
          newErrors.phone = '';
        }
        break;
      case 'address':
        if (!value) newErrors.address = t('Address_Is_Required');
        else newErrors.address = '';
        break;
      case 'city':
        if (!value) newErrors.city = t('City_Is_Required');
        else newErrors.city = '';
        break;
      case 'state':
        if (!value) newErrors.state = t('State_Is_Required');
        else newErrors.state = '';
        break;
      case 'zipCode':
        if (!value) newErrors.zipCode = t('Postal_Code_Is_Required');
        else newErrors.zipCode = '';
        break;
      case 'country':
        if (!value) newErrors.country = t('Country_Is_Required');
        else newErrors.country = '';
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    validate(name, value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    validate(name, value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = Object.keys(formData).every((key) => {
      validate(key, formData[key as keyof typeof formData] as string);
      return !errors[key as keyof typeof errors];
    });
    if (isValid) {
      console.log('Form submitted successfully');
    }
  };

  /*const isFormValid = () => {
    return (
      formData.name &&
      formData.lastName &&
      formData.email &&
      formData.password &&
      formData.phone &&
      formData.address &&
      formData.city &&
      formData.state &&
      formData.zipCode &&
      formData.country &&
      formData.agree
    );
  };*/

  const [activeTab, setActiveTab] = useState('basic');

  return (
    <section className="d-flex flex-column flex-lg-row w-100 vh-100 bg-default">
      {/* Left Side: Form */}
      <div className="w-100 w-lg-60 d-flex flex-column align-items-center justify-content-center p-4 p-sm-5">
        <div className="text-center mb-4">
          <h4 className="font-weight-bold display-5">
            {t('Register_Page.Request_To_Access')}
          </h4>
          <p className="text-gray-600">{t('Register_Page.We_Will_Review_Your_Request_And_Get_Back_To_You_Shortly')}</p>
        </div>

        <div className="nav nav-tabs mb-4">
          <button
            className={`nav-link ${activeTab === 'basic' ? 'text-orange' : 'text-dark'}`}
            onClick={() => setActiveTab('basic')}
          >
            {t('Register_Page.Basic_Information')}
          </button>
          <button
            className={`nav-link ${activeTab === 'address' ? 'text-orange' : 'text-dark'}`}
            onClick={() => setActiveTab('address')}
          >
            {t('Register_Page.Address_Information')}
          </button>
        </div>

        <form className="w-75 max-w-md mx-auto p-4 rounded" onSubmit={handleSubmit}>
          {activeTab === 'basic' && (
            <div className="form-row mb-3">
              {/* Basic Info Tab */}
              <div className="form-group col-sm-6">
                <input
                  type="text"
                  name="name"
                  placeholder={t('Register_Page.First_Name')}
                  className="form-control"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ outline: 'none', borderRadius: '8px' }}
                />
                {errors.name && <div className="text-danger">{errors.name}</div>}
              </div>
              <div className="form-group col-sm-6">
                <input
                  type="text"
                  name="lastName"
                  placeholder={t('Register_Page.Last_Name')}
                  className="form-control"
                  value={formData.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ outline: 'none', borderRadius: '8px' }}
                />
                {errors.lastName && <div className="text-danger">{errors.lastName}</div>}
              </div>
              <div className="form-group col-sm-6">
                <input
                  type="email"
                  name="email"
                  placeholder={t('Register_Page.Email')}
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ outline: 'none', borderRadius: '8px' }}
                />
                {errors.email && <div className="text-danger">{errors.email}</div>}
              </div>
              <div className="form-group col-sm-6">
                <input
                  type="password"
                  name="password"
                  placeholder={t('Register_Page.Password')}
                  className="form-control"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ outline: 'none', borderRadius: '8px' }}
                />
                {errors.password && <div className="text-danger">{errors.password}</div>}
              </div>
              <div className="form-group col-sm-6">
                <input
                  type="tel"
                  name="phone"
                  placeholder={t('Register_Page.Phone')}
                  className="form-control"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ outline: 'none', borderRadius: '8px' }}
                />
                {errors.phone && <div className="text-danger">{errors.phone}</div>}
              </div>
            </div>
          )}

          {activeTab === 'address' && (
            <div className="form-row mb-3">
              {/* Address Info Tab */}
              <div className="form-group col-sm-6">
                <input
                  type="text"
                  name="address"
                  placeholder={t('Register_Page.Address')}
                  className="form-control"
                  value={formData.address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ outline: 'none', borderRadius: '8px' }}
                />
                {errors.address && <div className="text-danger">{errors.address}</div>}
              </div>
              <div className="form-group col-sm-6">
                <input
                  type="text"
                  name="city"
                  placeholder={t('Register_Page.City')}
                  className="form-control"
                  value={formData.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ outline: 'none', borderRadius: '8px' }}
                />
                {errors.city && <div className="text-danger">{errors.city}</div>}
              </div>
              <div className="form-group col-sm-6">
                <input
                  type="text"
                  name="state"
                  placeholder={t('Register_Page.State')}
                  className="form-control"
                  value={formData.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ outline: 'none', borderRadius: '8px' }}
                />
                {errors.state && <div className="text-danger">{errors.state}</div>}
              </div>
              <div className="form-group col-sm-6">
                <input
                  type="text"
                  name="zipCode"
                  placeholder={t('Register_Page.Zip_Code')}
                  className="form-control"
                  value={formData.zipCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ outline: 'none', borderRadius: '8px' }}
                />
                {errors.zipCode && <div className="text-danger">{errors.zipCode}</div>}
              </div>
              <div className="form-group col-sm-6">
                <input
                  type="text"
                  name="country"
                  placeholder={t('Register_Page.Country')}
                  className="form-control"
                  value={formData.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ outline: 'none', borderRadius: '8px' }}
                />
                {errors.country && <div className="text-danger">{errors.country}</div>}
              </div>
            </div>
          )}

          {/* Terms & Conditions */}
          <div className="form-check mb-3">
            <input
              type="checkbox"
              id="agree"
              name="agree"
              className="form-check-input"
              checked={formData.agree}
              onChange={handleChange}
              style={{ color: '#1479d7' }}
            />
            <label htmlFor="agree" className="form-check-label font-weight-medium">
              {t('Register_Page.I_agree_to_the')}&nbsp;
              <a
            
                className="font-weight-normal cursor-pointer  transition-colors a_login "
              >
                {t('Register_Page.Terms_And_Conditions')}
              </a>
              &nbsp;{t('Register_Page.And')}&nbsp;
              <a
               
                className="font-weight-normal cursor-pointer  transition-colors a_login"
              >
                {t('Register_Page.Privacy_Policy')}
              </a>
            </label>
          </div>

          {/* Register Button */}
          <Button 
            type="submit" 
            variant="primary" 
            className="btn w-100 mt-3" 
            onClick={handleSubmit}
            
            style={{
              background: 'linear-gradient(90deg, rgb(242 187 70), rgb(200 112 28))',
              border: 'none',
              color: '#fff'
            }}
            onMouseOver={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.background = 'rgb(200 112 28)';
            }}
            onMouseOut={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.background = 'linear-gradient(90deg, rgb(242 187 70), rgb(200 112 28))';
            }}
          >
            {t('Register_Page.Send_Request')}
          </Button>

          {/* Already have an account */}
          <div className="text-center mt-3">
            <p className="text-gray-600">
              {t('Register_Page.Already_Have_An_Account')}&nbsp;
              <a href="/login" className="a_login">
                {t('Register_Page.Sign_In')}
              </a>
            </p>
          </div>
        </form>
      </div>

      {/* Right Side: Image */}
      <div className="w-100 w-lg-40 h-100">
        <img
          src="../assets/images/register.png"
          className="w-100 h-100 object-cover"
          alt={t('Request_Access')}
        />
      </div>
    </section>
  );
};

export default withTranslation()(Register);
